/**
 * Handing a generated file to the user, once.
 *
 * Two delivery paths, because phones and computers take files differently.
 * `<a download>` is the desktop and Android answer. On iOS it is unreliable —
 * Safari has historically ignored the attribute for blob URLs and rendered the
 * CSV as a page of text instead, and inside an installed PWA it can do nothing
 * visible at all. Where the platform offers a share sheet for files, use that:
 * the user gets Save to Files, Mail, or straight into whatever they meant to
 * paste it into.
 *
 * The "once" is the other half, and it has two distinct causes.
 *
 * The one that actually bit: a CSV export arrived on an iPhone as TWO files —
 * the CSV, and a .txt containing "Fixes for myself tasks". That string was the
 * `title` passed to navigator.share. **iOS attaches a share's title as a
 * separate text item**, so "Save to Files" writes both. One share, two files.
 * The fix is simply not to send a title alongside files; the filename already
 * carries the identity, and every other platform ignored it anyway.
 *
 * The duplicate window below is the *other* guard, kept because it is cheap
 * and covers a different failure — a control that fires twice on touch, or a
 * share that resolves and then falls through to the download. It could never
 * have caught the title bug, because there was only ever one call.
 */

/** How long two identical deliveries count as the same one. */
export const DUPLICATE_WINDOW_MS = 3000;

export type DeliveryResult =
  /** The share sheet took it. */
  | 'shared'
  /** A download was started. */
  | 'downloaded'
  /** The user dismissed the share sheet — a decision, not a failure. */
  | 'cancelled'
  /** A repeat of a delivery already in flight or just completed. */
  | 'suppressed';

/**
 * Is this the same delivery we just made?
 *
 * Pure so the window can be tested without a browser. `previousAt` is
 * undefined the first time; a clock that goes backwards (or a mocked one)
 * must not suppress forever, hence the lower bound.
 */
export function isDuplicateDelivery(
  previousAt: number | undefined,
  now: number,
  windowMs = DUPLICATE_WINDOW_MS,
): boolean {
  if (previousAt === undefined) return false;
  const elapsed = now - previousAt;
  return elapsed >= 0 && elapsed < windowMs;
}

/** Can this browser hand a File to the share sheet? */
export function canShareFile(file: File): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  // canShare is the only reliable answer; Android Chrome and iOS Safari both
  // tell the truth. Feature-detecting `share` alone is not enough — desktop
  // Safari has it and rejects files.
  return navigator.canShare?.({ files: [file] }) === true;
}

const lastDelivered = new Map<string, number>();

/** Test seam — the module keeps state deliberately, across components. */
export function resetDeliveryHistory(): void {
  lastDelivered.clear();
}

export interface DeliverFileOptions {
  filename: string;
  parts: BlobPart[];
  type: string;
  /** Injectable clock, for tests. */
  now?: () => number;
}

/**
 * What gets handed to navigator.share.
 *
 * Files ONLY — deliberately no `title`, and no `text`. iOS turns either into a
 * second item in the share sheet, so saving to Files produces a stray .txt
 * next to the real export. Exported so a test can hold that shape down.
 */
export function fileShareData(file: File): { files: File[] } {
  return { files: [file] };
}

export async function deliverFile({
  filename,
  parts,
  type,
  now = () => Date.now(),
}: DeliverFileOptions): Promise<DeliveryResult> {
  const at = now();
  if (isDuplicateDelivery(lastDelivered.get(filename), at)) return 'suppressed';
  // Claimed before either path runs, so a second call while the share sheet is
  // open — the case that produced two files — is suppressed rather than queued.
  lastDelivered.set(filename, at);

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      const file = new File(parts, filename, { type });
      if (canShareFile(file)) {
        await navigator.share(fileShareData(file));
        return 'shared';
      }
    } catch (error) {
      const name = (error as Error)?.name;
      // AbortError means the sheet was dismissed. Don't then shove a download
      // at someone who just said no.
      if (name === 'AbortError' || name === 'NotAllowedError') return 'cancelled';
      console.warn('[deliverFile] share failed, falling back to download:', error);
    }
  }

  const url = URL.createObjectURL(new Blob(parts, { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking synchronously can cancel a download Safari hasn't started
  // reading yet. A minute is far longer than any browser needs and still
  // bounded, so the blob doesn't sit in memory for the session.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return 'downloaded';
}
